import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { FC, useEffect, useState } from 'react';
import { Route } from 'react-router';
import { CareMapState } from '../caremap/CareMapState';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { InnerhivePage } from '../InnerhivePage';
import { CareMapNotesList } from './note-list/CareMapNotesList';
import { IonButtons, IonText, IonToolbar } from '@ionic/react';
import { FullWidthHeader } from '../components/FullWidthHeader';
import { SearchBar } from '../components/SearchBar';
import { AddNoteButton } from './add-note/AddNoteButton';
import { ViewNoteWithQueryParamDrawer } from './view-note/ViewNoteWithQueryParamDrawer';
import { CaremapNotesModelFactory } from './model/CaremapNotesModelFactory';
import { AddNoteWithQueryParamDrawer } from './add-note/AddNoteWithQueryParamDrawer';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CareTabsProvider } from '../care/CareTabsProvider';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { AddCareMapButton } from '../caremap/AddCareMapButton';
import { NotesSortControl, SortField, SortOrder } from './components/NotesSortControl';
import { NotesFilterControl, FilterState } from './components/NotesFilterControl';
import { RecordingState } from '../appointments/recording/RecordingState';
import { OfflineRecordingListItem } from '../appointments/recording/OfflineRecordingListItem';
import { sortByNewest } from '../utils/arrays';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { useBreakpoints } from '../theme/useBreakpoints';
import { filterNotes } from './utils/filterNotes';

export const NotesPageRouteHandler: React.FC = () => (
    <Route exact path="/notes/:caremapId" render={routeParams => (
        <CareMapNotesPage careMapId={routeParams.match.params.caremapId} />
    )} />
);

export const CareMapNotesPage: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <ActiveCaremapNotesPage />
        </ActiveCareMapProviderV2>
    );
};

export const ActiveCaremapNotesPage: React.FC = () => {
    const mixpanel = useInstance(MixPanelEventEmitter);

    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);
    const addNoteDrawer = useInstance(AddNoteWithQueryParamDrawer);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);
    const offlineRecordings = recordingState.getOfflineRecordingsForActiveCareMap();

    const tabsProvider = useInstance(CareTabsProvider);
    const appPrincipal = useInstance(AppPrincipal);

    const [sortField, setSortField] = useState<SortField>('updatedAt');
    const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
    const [filters, setFilters] = useState<FilterState>({
        noteType: [],
        careCategory: [],
        tags: []
    });
    const [searchText, setSearchText] = useState('');

    const { isBelowBreakpoint } = useBreakpoints();
    const isMobile = isBelowBreakpoint('md');

    const activeCaremap = careMapState.getActiveCaremap();

    useEffect(() => {
        mixpanel.track('Notes Page Opened');
    }, [mixpanel]);


    if (!activeCaremap){
        return null;
    }

    const incompleteCareMap = careMapState.getIncompleteCareMap();

    const allNotes = notesModelFactory.forActiveCaremap().getNotes();

    // Sort offline recordings by creation date (newest first)
    const sortedOfflineRecordings = sortByNewest(offlineRecordings, 'created');

    const notes = filterNotes(allNotes, filters, searchText);

    // Check if any filters are active
    const hasActiveFilters = filters.noteType.length > 0 || filters.careCategory.length > 0 || searchText.trim().length > 0;

    const canEdit = activeCaremap.links.self.actions.includes('edit-notes');

    const editNote = (noteId: string) => {
        viewNoteDrawer.open({ noteId });
    };

    const addNote = () => {
        addNoteDrawer.open();
    };

    const canAddNotes = careMapState.hasPermissionTo('post-note');

    // NOTE: heightOffset accounts for the fixed header containing switcher,
    // which is taller for anonymous due to expiration message, and for search bar when notes exist
    const isAnonymous = isAnonymousViewer(appPrincipal);
    const hasSearchBar = allNotes.length > 0;
    const heightOffset = 80 + (isAnonymous ? 28 : 0) + (hasSearchBar ? 52 : 0);

    return (
        <>
            <InnerhivePage title="Care" tabs={tabsProvider.getTabs()}>
                <FullWidthHeader fixed spacerHeight={(isMobile ? 55 : 45) + (isAnonymous ? 28 : 0) + (hasSearchBar ? 52 : 0)}>
                    <IonToolbar>
                        {incompleteCareMap ? (
                            <AddCareMapButton />
                        ): (
                            <>
                                <div style={{margin: '4px 12px 0 4px', paddingTop: 5, paddingBottom: 10}}>
                                    <CareRecipientSwitcher />
                                </div>
                                <IonButtons slot="end">
                                    {allNotes.length > 0 && (
                                        <>
                                            <NotesSortControl
                                                sortField={sortField}
                                                sortOrder={sortOrder}
                                                onSortFieldChange={setSortField}
                                                onSortOrderChange={setSortOrder}
                                                triggerId="sort-trigger-toolbar"
                                                iconOnly={isMobile}
                                            />
                                            <NotesFilterControl
                                                filters={filters}
                                                onFiltersChange={setFilters}
                                                notes={allNotes}
                                                triggerId="filter-trigger-toolbar"
                                                iconOnly={isMobile}
                                            />
                                        </>
                                    )}
                                    {canAddNotes && (
                                        <AddNoteButton
                                            useIcon
                                            canAdd={canEdit}
                                            onClick={addNote}
                                            iconOnly={isMobile}
                                        />
                                    )}
                                </IonButtons>

                            </>
                        )}
                    </IonToolbar>
                    {allNotes.length > 0 && (
                        <SearchBar
                            value={searchText}
                            onValueChange={(value) => {
                                setSearchText(value);
                                const searchEvent = 'Notes Search Used';
                                if (value) {
                                    mixpanel.trackWithDebounce({
                                        event: searchEvent,
                                        waitMS: 1000,
                                        getContext: () => ({
                                            'Search Query Length': value.length,
                                            'Results Count': filterNotes(allNotes, filters, value).length
                                        })
                                    });
                                } else {
                                    mixpanel.cancelDebouncedEvent(searchEvent);
                                }
                            }}
                            onClear={() => {
                                if (searchText) {
                                    mixpanel.track('Notes Search Cleared');
                                }
                            }}
                            placeholder="Search notes..."
                            data-id="notes-search"
                        />
                    )}
                </FullWidthHeader>

                {notesModelFactory.forActiveCaremap().hasLoaded && (
                    <>
                        {/* Show offline recordings section */}
                        {sortedOfflineRecordings.length > 0 && (
                            <div style={{ marginBottom: '24px' }} data-id="offline-recordings-list">
                                {sortedOfflineRecordings.map((recording) => (
                                    <OfflineRecordingListItem
                                        key={recording.id}
                                        recording={recording}
                                        showBottomLine={true}
                                    />
                                ))}
                            </div>
                        )}

                        {notes.length || canEdit ? (
                            <CareMapNotesList
                                notes={notes}
                                showHeading={false}
                                showNoteTargetHeader
                                onNoteClicked={editNote}
                                onAddNote={addNote}
                                showSortOptions={false}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                hasActiveFilters={hasActiveFilters}
                                heightOffset={heightOffset}
                                searchText={searchText}
                            />
                        ) : (
                            <IonText data-type="viewer-no-notes" color="medium" style={{display: 'block', paddingLeft: 12, paddingTop: 24}}>
                                No public notes yet… Check back soon!
                            </IonText>
                        )}
                    </>
                )}

            </InnerhivePage>
        </>
    );
};
